// Intercepts dynamic import failures caused by stale chunk hashes after a deploy.
// Runs in capture phase (before Sentry) to prevent noise for expected chunk misses.
// Anti-loop: reloads once per 30 s, tracked via sessionStorage + cookie fallback.
// Cookie fallback is critical for iOS Safari private mode where sessionStorage.setItem
// throws QuotaExceededError — the cookie survives page reloads in private mode.

const RELOAD_TS_KEY = 'sc_chunk_reload_ts';
const RELOAD_COOKIE  = 'sc_crl'; // compact name — written on every reload attempt
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
    'importing a module script failed',
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'unable to preload css for',
] as const;

// Safari/Firefox chunk-load error fragments — ambiguous on their own (overlap with real bugs),
// so isChunkLoadError requires a hashed-chunk stack trace when these match.
const SAFARI_CHUNK_PATTERNS = [
    'undefined is not an object (evaluating \'',
    '._result.default',
    'cannot destructure',
] as const;

// Matches a Vite hashed chunk filename: e.g. "Component-aB3dE4fG.js"
const HASHED_CHUNK_RE = /-[a-zA-Z0-9]{6,8}\.js/;

// In-memory timestamp — valid only within the current page load. Destroyed on reload.
let lastReloadTsMem = 0;

// Exported so AppErrorBoundary can reuse the same detection + anti-loop logic without
// duplicating state. Both share the same sessionStorage key + cookie + in-memory fallback.
export function isChunkLoadError(reason: unknown): boolean {
    // Accept both TypeError (unhandledrejection path) and plain Error (React.lazy re-throw path).
    // React absorbs the lazy() rejection and re-throws it as a render error — which may not be
    // a TypeError — so we check the message regardless of constructor.
    if (!(reason instanceof Error)) return false;
    const msg = reason.message.toLowerCase();

    // Chrome/Firefox unambiguous chunk patterns — message alone is sufficient.
    if (CHUNK_ERROR_PATTERNS.some(pattern => msg.includes(pattern))) return true;

    // Safari patterns overlap with real programming bugs — require a hashed chunk in the stack
    // to distinguish a stale-deploy miss from an actual null-dereference in app code.
    const stack = reason.stack ?? '';
    if (
        SAFARI_CHUNK_PATTERNS.some(pattern => msg.includes(pattern)) &&
        HASHED_CHUNK_RE.test(stack)
    ) return true;

    return false;
}

function getLastReloadTs(): number {
    // 1. sessionStorage — works in normal mode; getItem returns null (no throw) in private mode.
    try {
        const val = sessionStorage.getItem(RELOAD_TS_KEY);
        if (val !== null) return parseInt(val, 10);
    } catch { /* sessionStorage fully unavailable — fall through */ }
    // 2. Cookie — survives page reload in iOS Safari private mode (only setItem throws there).
    try {
        const match = document.cookie.match(/(?:^|;\s*)sc_crl=([^;]*)/);
        if (match?.[1]) return parseInt(match[1], 10);
    } catch { /* cookies blocked — fall through */ }
    // 3. In-memory — same page load only; reset to 0 after any reload.
    return lastReloadTsMem;
}

export function markReloaded(): void {
    const now = Date.now();
    lastReloadTsMem = now;
    try {
        sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    } catch { /* private mode — sessionStorage.setItem throws QuotaExceededError */ }
    // Cookie fallback: 60 s max-age covers the full reload + boot cycle.
    // iOS Safari private mode: setItem throws but cookies are writable.
    try {
        document.cookie = `${RELOAD_COOKIE}=${now}; max-age=60; path=/; SameSite=Strict`;
    } catch { /* very rare — cookie write blocked */ }
}

export function wasRecentlyReloaded(): boolean {
    return Date.now() - getLastReloadTs() < RELOAD_COOLDOWN_MS;
}

export function handleBootstrapModuleError(err: unknown): boolean {
    if (!navigator.onLine) return false;
    if (wasRecentlyReloaded()) return false;
    if (!(err instanceof TypeError)) return false;
    const msg = err.message.toLowerCase();
    // Safari: "undefined is not an object (evaluating 'x.y')"
    // Chrome: "cannot read properties of undefined (reading 'default')"
    // Both: "is not a constructor" — module loaded with wrong shape (stale interop)
    const isStaleExport =
        msg.includes('undefined is not an object') ||
        msg.includes('cannot read properties of undefined') ||
        msg.includes('is not a constructor');
    if (!isStaleExport) return false;
    // Require the error to originate from a Vite hashed chunk (name-hash8chars.js).
    // Filters real programming bugs in app code — which emit the same TypeError messages
    // but typically originate from source-mapped files, not lazy-loaded chunk files.
    const stack = err.stack ?? '';
    if (!/[A-Za-z][A-Za-z0-9_-]*-[A-Za-z0-9_-]{8}\.js/.test(stack)) return false;
    markReloaded();
    window.location.reload();
    return true;
}

export function initChunkErrorHandler(): void {
    // Path 1: unhandledrejection — covers imperative import() calls in AdminRouter / MainRouter.
    window.addEventListener(
        'unhandledrejection',
        (event: PromiseRejectionEvent) => {
            if (!isChunkLoadError(event.reason)) return;

            if (!navigator.onLine) {
                // User is offline — suppress noise, don't reload
                event.preventDefault();
                return;
            }

            if (wasRecentlyReloaded()) {
                // Already reloaded once and it failed again — let Sentry capture as real issue
                return;
            }

            // Handled: suppress the error and force a hard reload with fresh HTML + chunks
            event.preventDefault();
            markReloaded();
            window.location.reload();
        },
        { capture: true }, // runs before Sentry and other bubble-phase handlers
    );

    // Path 2: vite:preloadError — Vite's official event for modulepreload failures.
    // Covers the gap where <link rel="modulepreload"> fails before React.lazy even runs.
    // VitePreloadErrorEvent is a global declared in vite/client.d.ts (already referenced via global.d.ts).
    window.addEventListener('vite:preloadError', (event: VitePreloadErrorEvent) => {
        if (!isChunkLoadError(event.payload)) return;
        if (!navigator.onLine) return;
        if (wasRecentlyReloaded()) return;
        event.preventDefault();
        markReloaded();
        window.location.reload();
    });
}
